import type { Participant } from '@live-stream/shared'
import type { Rooms } from '../api/rooms.js'
import type { Auth } from '../auth.js'
import { ApiError } from '../http.js'
import type { Media } from '../media/media.js'
import type { Room } from '../room.js'
import { derived, patch, readonly, watch, writable, type ReadableStore } from '../store.js'

/**
 * The call, from the green room to leaving: one meeting page's worth of
 * orchestration over the room and the media plane.
 *
 * - Before joining: what the code is (its name, whether a lobby stands in
 *   front, where the SFU is), and joining in the one order that works.
 * - Once in: publishing, then keeping the room's idea of our microphone
 *   and camera true to the hardware (and the hardware to a host's mute),
 *   reporting speaking and quality, re-reporting after a reconnect, and
 *   starting the media plane over when the server hands us a new peer id.
 *
 * `open(slug)` when the meeting page opens, `close()` when it goes.
 */

export interface CallState {
  slug: string | null
  /** Looking the code up. */
  checking: boolean
  /** The room's name, once looked up. */
  title: string
  /** A lobby stands between guests and the room. */
  lobbyEnabled: boolean
  /** Where the SFU is, when this server has one. Decides the transport before the join. */
  sfuUrl: string | null
  /**
   * Whether attendees may turn their mic and camera on, as the room was
   * when looked up — for the green room to say so before the join. Null
   * until known.
   */
  attendees: { mic: boolean; camera: boolean } | null
  /** The signed-in person owns the room (its host): the room's switches never hold them. */
  owner: boolean
  /**
   * We tapped a mic or camera a big meeting keeps off for attendees: the
   * notice says how to ask (raise a hand). Null once seen.
   */
  needsInvite: 'mic' | 'camera' | null
}

export type PrejoinStatus = 'ready' | 'connecting' | 'waiting'

export class Call {
  private readonly store = writable<CallState>({
    slug: null,
    checking: false,
    title: '',
    lobbyEnabled: true,
    sfuUrl: null,
    attendees: null,
    owner: false,
    needsInvite: null,
  })
  readonly state: ReadableStore<CallState> = readonly(this.store)
  /** Only a hint for the button; the server decides who waits. Owners walk straight in. */
  readonly joinLabel: ReadableStore<'Ask to join' | 'Join now'>
  /** Where the green room's join stands. */
  readonly prejoinStatus: ReadableStore<PrejoinStatus>
  /**
   * One banner for three situations, worded for what the person will
   * notice. Socket down, media up: they can still hear everyone. Media
   * down, socket up: everyone went quiet but the room is intact. Both:
   * their network is out.
   */
  readonly reconnectNotice: ReadableStore<string | null>
  /**
   * Everyone in the meeting as this client shows them: others as the server
   * broadcasts them, ourselves speaking and connected as our own detector
   * and poll say, instantly, with no round trip.
   */
  readonly people: ReadableStore<Participant[]>
  /** Raised hands, first up first: the order a host should take them in. */
  readonly hands: ReadableStore<Participant[]>
  /** We are presenting. */
  readonly sharing: ReadableStore<boolean>

  private stops: Array<() => void> = []

  constructor(
    private readonly rooms: Rooms,
    private readonly auth: Auth,
    private readonly room: Room,
    private readonly media: Media,
  ) {
    this.joinLabel = derived([this.store, auth.signedIn], (s, signedIn) =>
      s.lobbyEnabled && !signedIn ? 'Ask to join' : 'Join now',
    )
    const phase = derived([room.state], (r) => r.phase)
    this.prejoinStatus = derived([phase], (p) =>
      p === 'connecting' ? 'connecting' : p === 'waiting' ? 'waiting' : 'ready',
    )
    const link = derived([room.state], (r) => r.link)
    this.reconnectNotice = derived([link, media.allLinksDown], (l, mediaDown) => {
      const socketDown = l === 'reconnecting'
      if (socketDown && mediaDown) return 'Your connection dropped. Reconnecting…'
      if (socketDown) {
        return 'Connection to the meeting server lost. Reconnecting… your call continues meanwhile.'
      }
      if (mediaDown) return 'Lost the connection to the others. Reconnecting…'
      return null
    })
    const me = derived([room.state], (r) => r.me)
    const quality = derived([media.state], (m) => m.quality)
    this.people = derived(
      [room.participants, me, media.speaking, quality],
      (list, meId, speaking, q) =>
        list.map((p) =>
          p.peerId === meId ? { ...p, speaking, connectionQuality: q ?? p.connectionQuality } : p,
        ),
    )
    this.hands = derived([room.participants], (people) =>
      people
        .filter((p) => p.handRaised)
        .sort((a, b) => (a.handRaisedAt ?? 0) - (b.handRaisedAt ?? 0)),
    )
    this.sharing = derived([media.state], (m) => m.share !== null)
  }

  private get joined(): boolean {
    return this.room.state.get().phase === 'joined'
  }

  /**
   * The meeting page opened on `slug`: look the code up, and follow the
   * room until `close()`. Nobody joins here; the green room opens the
   * camera and mic for a preview, and the person decides when to join.
   */
  async open(slug: string): Promise<void> {
    this.close()
    patch(this.store, {
      slug,
      checking: true,
      title: '',
      lobbyEnabled: true,
      sfuUrl: null,
      attendees: null,
      owner: false,
      needsInvite: null,
    })
    this.follow()
    try {
      const info = await this.rooms.get(slug)
      patch(this.store, {
        title: info.room.name,
        lobbyEnabled: info.room.lobbyEnabled,
        sfuUrl: info.sfu?.url ?? null,
        attendees: info.room.attendees ?? null,
        owner: info.room.isOwner ?? false,
      })
    } catch (err) {
      // Only a 404 means the code is wrong. Anything else (a tunnel rate
      // limit, an interstitial, a dead backend) is a different problem with
      // a different fix, and calling it "does not exist" sends people to
      // re-check a code that was right all along.
      this.room.fail(
        err instanceof ApiError && err.status === 404
          ? 'That meeting code does not exist.'
          : `Could not reach the meeting (${err instanceof Error ? err.message : String(err)}). ` +
              `The code may be fine — try reloading.`,
      )
    } finally {
      patch(this.store, { checking: false })
    }
  }

  /** The green room's button. Guests claim a room-scoped pass first; it costs a name. */
  async join(name: string): Promise<void> {
    const slug = this.store.get().slug
    if (!slug) return
    if (!this.auth.isAuthenticated) {
      if (!(await this.auth.claimGuestPass(slug, name))) {
        this.room.fail(this.auth.state.get().error)
        return
      }
    }
    await this.enter(slug)
  }

  /**
   * The order here is the whole fix for "both people see only themselves".
   *
   * 1. Connect the socket (we need its id).
   * 2. Subscribe the media transport to signaling: NOT awaited, and with
   *    no camera involved. The server announces peers and the first offer
   *    arrives within milliseconds of the join ack; getUserMedia can take
   *    seconds.
   * 3. Only then ask to join.
   */
  private async enter(slug: string): Promise<void> {
    let ready: Awaited<ReturnType<Room['ensureSocket']>>
    try {
      ready = await this.room.ensureSocket()
    } catch {
      return // ensureSocket already set phase and message
    }
    void this.media.prepare(ready.sock, ready.peerId, { sfuUrl: this.store.get().sfuUrl })
    await this.room.join(slug)
  }

  /** Leave the meeting; the page goes elsewhere. */
  leave(): void {
    this.media.stop()
    this.room.leave()
  }

  /** The meeting page is going: stop following, and leave if still in (or waiting). */
  close(): void {
    for (const stop of this.stops) stop()
    this.stops = []
    if (!this.store.get().slug) return
    this.media.stop()
    const phase = this.room.state.get().phase
    // Ended, the socket was kept for a recording being saved: let it go now.
    if (phase === 'joined' || phase === 'waiting' || phase === 'ended') this.room.leave()
    patch(this.store, { slug: null })
  }

  private follow(): void {
    const { room, media } = this
    this.stops = [
      // Publishing starts only once we are actually in the room. The green
      // room already opened the camera and mic, by the person's own choice,
      // so while they wait in the lobby the preview stays up and nothing is
      // sent. Signaling was subscribed in enter().
      watch(
        room.state,
        (s) => s.phase,
        (phase) => {
          if (phase === 'joined') void this.startMedia()
          // The meeting is over, or we are out of it — removed, turned away, or
          // this seat taken over by the same person in another tab: the camera
          // and mic go at once, lights out, and nothing of ours keeps playing.
          if (phase === 'ended' || phase === 'denied') this.media.stop()
        },
        { immediate: true },
      ),
      // Speaking is reported as transitions only, never a level stream, and
      // the server turns each one into a one-participant patch for everyone
      // else. Our own ring follows the local detector directly.
      watch(
        media.speaking,
        (s) => s,
        (speaking) => {
          if (this.joined) room.setSpeaking(speaking)
        },
      ),
      // Connection quality, the same way: a two-second poll with hysteresis
      // on our side, transitions only on the wire.
      watch(
        media.state,
        (s) => s.quality,
        (quality) => {
          if (quality && this.joined) room.setQuality(quality)
        },
      ),
      // A mute arriving as state (a host's, or the room's mute-on-entry
      // rule). The state is authoritative for the fact; the hardware still
      // has to follow it, or the person keeps sending audio under a "muted"
      // label. Only the muting direction is followed: nothing a host does
      // ever turns someone's mic on.
      watch(
        room.self,
        (p) => p?.audio.muted,
        (muted) => {
          if (muted && !media.state.get().micMuted) media.setMuted(true)
        },
      ),
      // A big meeting's switches, and a host letting us speak: what we may
      // send decides what the hardware does. Losing the mic closes it;
      // losing the camera turns it off. Gaining either turns nothing on —
      // that is ours to do (the notice offers it).
      watch(
        room.caps,
        (c) => c.mic,
        (mayTalk) => void media.setMicAllowed(mayTalk),
        { immediate: true },
      ),
      watch(
        room.caps,
        (c) => c.camera,
        (mayShow) => void media.setCameraAllowed(mayShow),
        { immediate: true },
      ),
      // The same for a camera a host turned off: the hardware follows.
      watch(
        room.self,
        (p) => p?.video.stoppedByHost === true && !p.video.enabled,
        (stopped) => {
          if (stopped && media.local.state.get().videoTrack) void media.setCameraEnabled(false)
        },
      ),
      // Reconnection: the socket came back and the server answered our join,
      // the same seat or a fresh one. Either way its idea of our media may be
      // stale, so the hardware truth is re-reported.
      watch(
        room.state,
        (s) => s.rejoinCount,
        () => void this.syncMediaState(),
      ),
      // The camera source stopped delivering frames (a backgrounded phone, a
      // locked screen, a camera another app took) while the track is live.
      // Tell the room "camera off" rather than leave a frozen face, and "on"
      // again the moment frames return. A camera turned off on purpose has
      // no track, and is left alone.
      watch(
        media.local.state,
        (s) => s.videoPaused,
        (paused) => {
          if (!this.joined || !media.local.state.get().videoTrack) return
          void room.setCamera(!paused)
        },
      ),
      // All links down at once is our network. The socket may not have
      // noticed yet; probe it now rather than wait out the heartbeat.
      watch(
        media.allLinksDown,
        (down) => down,
        (down) => {
          if (down) void room.probeConnection()
        },
      ),
      // The server could not give us our peer id back (a duplicated tab, an
      // expired resume token). Roles hang off that id, so the media plane
      // starts over under the new one; syncMediaState re-publishes once the
      // join lands.
      room.onIdentityChanged((peerId, sock) => {
        media.stop()
        void media.prepare(sock, peerId, { sfuUrl: this.store.get().sfuUrl })
      }),
    ]
  }

  /** The shape of the camera we hold (width over height), once it says: for the others' layout. */
  private cameraAspect(): number | undefined {
    const settings = this.media.local.state.get().videoTrack?.getSettings?.()
    const w = settings?.width
    const h = settings?.height
    return w && h ? Math.round((w / h) * 1000) / 1000 : undefined
  }

  /** Camera on or off to the room; on, with the picture's shape when it is known. */
  private announceCamera(on: boolean): ReturnType<Room['setCamera']> {
    const aspect = on ? this.cameraAspect() : undefined
    return aspect === undefined ? this.room.setCamera(on) : this.room.setCamera(on, aspect)
  }

  /**
   * Publish, then reconcile the room's flags with reality. The server's
   * entry rules win first: a room that starts everyone camera-off arrives
   * as video.enabled=false, and the camera is stopped to match. Then our
   * side: a camera that is off (blocked, missing, or turned off in the
   * green room) is reported off, and a mic that is missing or muted is
   * reported muted, so nobody waits for video from someone who will never
   * send it.
   */
  private async startMedia(): Promise<void> {
    const { room, media } = this
    await media.start()
    if (!this.joined) return
    const me = room.self.get()
    const local = media.local.state.get()
    const caps = room.caps.get()
    if ((me?.video.enabled === false || !caps.camera) && local.videoTrack) {
      void media.setCameraEnabled(false)
    }
    if (!local.videoTrack && me?.video.enabled !== false) void room.setCamera(false)
    // A camera we are sending: say its shape now, before a frame has reached anyone.
    const aspect = this.cameraAspect()
    if (aspect !== undefined && me?.video.enabled !== false && caps.camera) {
      void this.announceCamera(true)
    }
    if ((!local.audioTrack || media.state.get().micMuted) && !me?.audio.muted) {
      void room.setMuted(true)
    }
  }

  private async syncMediaState(): Promise<void> {
    const { room, media } = this
    if (!this.joined) return
    await media.start() // a no-op unless the media plane was reset
    await media.ensureSfuConnected() // the rejoin brought a fresh token; reconnect if the SFU let go
    void media.reannounceShare()
    // What we may send may have changed while we were away.
    if (!room.caps.get().camera && media.local.state.get().videoTrack) {
      await media.setCameraEnabled(false)
    }
    const local = media.local.state.get()
    void this.announceCamera(local.videoTrack !== null)
    const m = media.state.get()
    const muted = m.micMuted || !local.audioTrack
    if (muted || !room.self.get()?.audio.mutedByHost) void room.setMuted(muted)
    if (m.quality) room.setQuality(m.quality)
  }

  /**
   * Camera off is instant at the hardware, so the room is told at the same
   * moment. On opens the camera first: announcing one we failed to open
   * would show everyone a tile that never fills.
   */
  async toggleCamera(): Promise<void> {
    const me = this.room.self.get()
    const next = !me?.video.enabled
    // A camera a host turned off stays off until they allow it.
    if (next && me?.video.stoppedByHost) return
    // So does one a big meeting keeps off: the person is told how to ask.
    if (next && !this.room.caps.get().camera) {
      patch(this.store, { needsInvite: 'camera' })
      return
    }
    if (!next) {
      await Promise.all([this.media.setCameraEnabled(false), this.room.setCamera(false)])
      return
    }
    if (await this.media.setCameraEnabled(true)) {
      await this.announceCamera(true)
    }
  }

  /** Mute or unmute; nothing while a host holds the mute. */
  async toggleMute(): Promise<void> {
    const me = this.room.self.get()
    if (me?.audio.mutedByHost) return
    const next = !me?.audio.muted
    if (!next && !this.room.caps.get().mic) {
      patch(this.store, { needsInvite: 'mic' })
      return
    }
    // Just let speak: the mic may still be opening.
    if (!next) await this.media.whenMicReady()
    this.media.setMuted(next)
    await this.room.setMuted(next)
  }

  /**
   * Unmute, whatever the room last said about us. The mic button toggles from the room's flag;
   * the reminder when we talk into a muted mic is about the mic itself, and must open it even
   * if the two have drifted apart (the room thinking we are heard while the mic is shut).
   */
  async unmute(): Promise<void> {
    const me = this.room.self.get()
    if (me?.audio.mutedByHost) return
    if (!this.room.caps.get().mic) {
      patch(this.store, { needsInvite: 'mic' })
      return
    }
    await this.media.whenMicReady()
    this.media.setMuted(false)
    await this.room.setMuted(false)
  }

  /** Ask to speak: raise our hand, for a host to let us. */
  async askToSpeak(): Promise<void> {
    patch(this.store, { needsInvite: null })
    if (!this.room.self.get()?.handRaised) await this.room.raiseHand(true)
  }

  dismissNeedsInvite(): void {
    patch(this.store, { needsInvite: null })
  }

  /**
   * A host let us speak: turn on what we choose from the notice — the mic,
   * the camera, or both — and put the notice away.
   */
  async acceptInvite(what: { mic?: boolean; camera?: boolean }): Promise<void> {
    this.room.dismissInvite()
    const me = this.room.self.get()
    if (what.mic && me?.audio.muted) await this.toggleMute()
    if (what.camera && !me?.video.enabled) await this.toggleCamera()
  }

  /** Yes to a host's ask: turn on what they asked for. */
  async acceptAsk(): Promise<void> {
    const asked = this.room.state.get().asked
    this.room.dismissAsk()
    const me = this.room.self.get()
    if (asked?.kind === 'mic' && me?.audio.muted) await this.toggleMute()
    if (asked?.kind === 'camera' && !me?.video.enabled) await this.toggleCamera()
  }

  /**
   * Present, or stop. A new presentation replaces someone else's for
   * everyone, as in Meet, so `replace(who)` is asked first; it returns
   * whether to go ahead.
   */
  async togglePresenting(replace: (who: string) => boolean = () => true): Promise<void> {
    if (this.sharing.get()) {
      await this.media.stopShare('user')
      return
    }
    const room = this.room.shared.get()
    const current = room.screenShare
    if (current && current.peerId !== this.room.state.get().me) {
      const who = room.participants[current.peerId]?.displayName ?? 'Someone'
      if (!replace(who)) return
    }
    await this.media.startShare()
  }
}
